import { type ReactNode } from 'react';
import { Label } from '@/components/ui/label';

/**
 * Side-panel primitives used on detail pages (TaskDetailPage,
 * NoteDetailPage). Extracted out of TaskDetailPage so the two detail
 * surfaces stay visually identical without copy-paste drift.
 *
 * The intended layout is:
 *
 *   <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
 *     <div className="min-w-0 space-y-6">…main content…</div>
 *     <aside className="space-y-4 lg:sticky lg:top-2 lg:self-start">
 *       <PanelCard title="Properties">
 *         <PanelStackedRow label="Status">…</PanelStackedRow>
 *       </PanelCard>
 *       <PanelCard title="Activity">
 *         <PanelInlineRow label="Created" value={…} />
 *       </PanelCard>
 *     </aside>
 *   </div>
 */

interface PanelCardProps {
  title: string;
  children: ReactNode;
}

export const PanelCard = ({ title, children }: PanelCardProps) => (
  <section className="space-y-3 rounded-xl border border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] p-4 shadow-[var(--glass-card-shadow)] backdrop-blur-md">
    <h3 className="text-foreground-faint text-[11px] font-semibold tracking-[0.06em] uppercase">
      {title}
    </h3>
    <div className="space-y-3">{children}</div>
  </section>
);

interface PanelStackedRowProps {
  label: string;
  children: ReactNode;
}

/** Label-on-top layout — for editable controls that need their own line. */
export const PanelStackedRow = ({ label, children }: PanelStackedRowProps) => (
  <div className="space-y-1">
    <Label className="text-foreground-faint text-[11px] font-medium tracking-[0.06em] uppercase">
      {label}
    </Label>
    {children}
  </div>
);

interface PanelInlineRowProps {
  label: string;
  value: ReactNode;
}

/** Compact label/value row — for read-only metadata like "Created Apr 28". */
export const PanelInlineRow = ({ label, value }: PanelInlineRowProps) => (
  <div className="flex items-center justify-between gap-3 text-[12.5px]">
    <span className="text-foreground-faint">{label}</span>
    <span className="text-foreground min-w-0 truncate text-right">{value}</span>
  </div>
);
